import type { Filters } from '@hlt/contracts';
import { periods, type Period } from '@hlt/domain';

/**
 * A report page's choices kept in the URL, so it can be bookmarked or sent to a colleague (who still sees
 * only their own permitted data): the period and the two or three compared audits, by their short public
 * identifiers. Older addresses that name audit revisions by UUID are still accepted. Dashboards have no
 * viewer filters; their period is a widget setting.
 */
export interface AnalyticsSearch {
    period?: Period;
    audits?: string;
}

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const publicIdPattern = /^[0-9A-Za-z]{11}$/;

/** Tolerant search parser: unknown or malformed values are dropped instead of failing the route. */
export function parseAnalyticsSearch(raw: Record<string, unknown>): AnalyticsSearch {
    const ids =
        typeof raw.audits === 'string'
            ? [
                  ...new Set(raw.audits.split(',').filter((id) => uuidPattern.test(id) || publicIdPattern.test(id))),
              ].slice(0, 3)
            : [];
    const search: AnalyticsSearch = {
        period: periods.find((p) => p === raw.period),
        audits: ids.length ? ids.join(',') : undefined,
    };
    return Object.fromEntries(Object.entries(search).filter(([, v]) => v !== undefined)) as AnalyticsSearch;
}

export const splitIds = (value: string | undefined): string[] => (value ? value.split(',').filter(Boolean) : []);

/** The report choices as API filters; two or three audit revisions make a comparison. */
export function searchToFilters(search: AnalyticsSearch, auditRevisionIds: string[] = []): Filters {
    const audits = auditRevisionIds;
    return {
        ...(search.period ? { period: search.period } : {}),
        ...(audits.length >= 2 ? { auditRevisionIds: audits } : {}),
    };
}
